import { CSSProperties, FormEvent, MouseEvent, useContext, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import Button from '../../../../../components/atoms/button';
import IconButton from '../../../../../components/atoms/button/IconButton';
import Input from '../../../../../components/atoms/input';
import TextArea from '../../../../../components/atoms/text-area/TextArea';
import {
  ImageArea,
  InteractiveImageContentData,
  InteractiveImageSpecificProps,
} from '../../../../../types/contentTypes';
import AlertContext from '../../../../../utils/AlertContext';
import { ContentFormProps } from '../../ContentEditor';
import inputFields from '../../inputFields.json';

import './InteractiveImageContentForm.scss';
import CopyrightInfoCard from './CopyrightInfoCard';

export default function InteractiveImageContentForm({
  content,
  onNewContentSubmit,
  onExistingContentSubmit,
}: ContentFormProps<InteractiveImageContentData, InteractiveImageSpecificProps>) {
  const [headline, setHeadline] = useState<string>(content?.headline || '');
  const [imageUrl, setImageUrl] = useState<string>(content?.imageUrl || '');
  const [areas, setAreas] = useState<ImageArea[]>(content?.areas || []);
  const [showCopyrightInfo, setShowCopyrightInfo] = useState<boolean>(false);

  const showAlert = useContext(AlertContext);

  const AreaButtons = areas.map((area, i) => {
    const style: CSSProperties = {
      position: 'absolute',
      left: `${area.placement[0] * 100}%`,
      top: `${area.placement[1] * 100}%`,
      width: '9%',
      aspectRatio: '1 / 1',
      backgroundColor: 'cornflowerblue',
      transform: 'translateY(-50%) translateX(-50%)',
      borderRadius: '9999px',
    };
    return (
      <button key={area.key} style={style} type="button" onClick={() => removeArea(i)}>
        {i + 1}
      </button>
    );
  });

  const AreaTexts = areas.map((area, n) => (
    <div key={area.key} className="flex pl-4">
      <p className="mr-4">{n + 1}</p>
      <TextArea
        className="mb-8 w-full"
        {...inputFields.areaText}
        idSuffix={n.toString()}
        value={area.text}
        onChange={e => handleAreaTextChange(e.target.value, n)}
      />
    </div>
  ));

  function addArea(placement: [number, number]) {
    setAreas([
      ...areas,
      {
        key: uuidv4(),
        placement,
        text: '',
      },
    ]);
  }

  function removeArea(index: number) {
    setAreas(areas.filter((_, i) => i !== index));
  }

  function handleClearClick() {
    setAreas([]);
  }

  function handleAreaTextChange(text: string, index: number) {
    const updatedAreas = areas.map((area, i) => (i === index ? { ...area, text } : area));
    setAreas(updatedAreas);
  }

  function handleImageClick(e: MouseEvent<HTMLImageElement>) {
    const image = e.currentTarget;
    const boundingRect = image.getBoundingClientRect();
    const clickedX = e.clientX - boundingRect.left;
    const clickedY = e.clientY - boundingRect.top;
    const scaledX = clickedX / image.width;
    const scaledY = clickedY / image.height;
    addArea([scaledX, scaledY]);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!content) {
      onNewContentSubmit?.({
        headline,
        areas,
        imageUrl,
      });
    } else {
      onExistingContentSubmit?.({
        ...content,
        headline,
        areas,
        imageUrl,
      });
    }
    showAlert('Changes saved!');
  }

  return (
    <div className="interactive-image-section-form">
      <form className="flex flex-col" onSubmit={handleSubmit}>
        <div className="">
          <label className="block mb-2">Enter headline</label>
          <Input className="mb-8 w-full" {...inputFields.headline} state={[headline, setHeadline]} />
        </div>
        <div className="">
          <label className="block mb-2">Image URL</label>
          <Input className="mb-8 w-full" {...inputFields.imageUrl} state={[imageUrl, setImageUrl]} />
          <div className="copyright-image-info-container">
            <button type="button" onClick={() => !showCopyrightInfo && setShowCopyrightInfo(true)}>
              Where can i find images to use?
            </button>
            {showCopyrightInfo && <CopyrightInfoCard onClose={() => setShowCopyrightInfo(false)} />}
          </div>
        </div>
        {imageUrl && (
          <>
            <p className="mb-4">
              Click the image to add clickable areas. Click on existing areas to remove them. Click &quot;clear&quot; to
              remove all areas.
            </p>
            <Button className="self-start mb-2" disabled={areas.length === 0} onClick={handleClearClick}>
              Clear
            </Button>
            <div className="inline-block relative self-center mb-8">
              <img onClick={handleImageClick} src={imageUrl} alt="upload" />
              {AreaButtons}
            </div>
          </>
        )}
        <p className="block mb-4">Area texts</p>
        {AreaTexts}
        <IconButton
          className="self-start mt-8"
          icon="save-outline"
          label={!content ? 'Save and add to section' : 'Save changes'}
          type="submit"
        />
      </form>
    </div>
  );
}
